
// One detected line of a supplier document next to what the matcher proposes for it, inside
// the document's detail view. The reviewer adds it, picks another product, or marks the row
// as "not a product" (fee, deposit, discount).

interface DocumentExtractionReviewRow
{
    index:number;
    line:any;               // the extraction line as the engine read it
    proposal:any;           // the matcher's line proposal (may be null)
    product:Product;        // the product that will be booked (proposal or pick)
    conceptItem:ConceptItem; // the invoice concept the line is booked to when it is not a product
    inputFormat:StockInputFormat;
    tax:Tax;
    notAProduct:boolean;
    skip:boolean;
    picked:boolean;         // the reviewer chose the product
    applied:boolean;        // the line was created on the document
    taxUnmatched?:boolean;  // the line's tax rate is none of the venue's taxes
    selected?:boolean;      // chosen for a custom line
    grouped?:boolean;       // stands behind a custom line (not booked on its own)
    group?:DocumentExtractionReviewRow[];   // a custom line: the detected rows it stands for
    customName?:string;     // the concept typed for a custom line that has no concept item yet
    remember?:boolean;      // a custom line to keep as the supplier's option once created
}

interface DocumentExtractionLineCellDelegate
{
    pickProductForRow(cell:DocumentExtractionLineCell, row:DocumentExtractionReviewRow) : void;
    toggleNotAProductForRow(row:DocumentExtractionReviewRow) : void;
    addRow(row:DocumentExtractionReviewRow) : void;
}

class DocumentExtractionLineCell extends UITableViewCell
{
    /// Labels render HTML; text read from a supplier's document is shown as text.
    static escape(text:any) : string {
        return String(text ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    }

    delegate:DocumentExtractionLineCellDelegate = null;

    private descriptionLabel:MUILabel = null;
    private printedLabel:MUILabel = null;
    private productLabel:MUILabel = null;
    private statusLabel:MUILabel = null;
    private detailLabel:MUILabel = null;
    private addButton:MUIButton = null;
    private pickButton:MUIButton = null;
    private notProductButton:MUIButton = null;

    awakeFromHTML(){
        this.descriptionLabel = MUIOutlet(this, "description-lbl", "MUILabel");
        this.printedLabel     = MUIOutlet(this, "printed-lbl", "MUILabel");
        this.productLabel     = MUIOutlet(this, "product-lbl", "MUILabel");
        this.statusLabel      = MUIOutlet(this, "status-lbl", "MUILabel");
        this.detailLabel      = MUIOutlet(this, "detail-lbl", "MUILabel");

        // the buttons exist on the supplier note rows only; the informative view has none
        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        if (this.addButton) this.addButton.setAction(this, function(this:DocumentExtractionLineCell){
            if (this.delegate != null) this.delegate.addRow(this._row);
        });
        this.pickButton = MUIOutlet(this, "pick-btn", "MUIButton");
        if (this.pickButton) this.pickButton.setAction(this, function(this:DocumentExtractionLineCell){
            if (this.delegate != null) this.delegate.pickProductForRow(this, this._row);
        });
        this.notProductButton = MUIOutlet(this, "not-product-btn", "MUIButton");
        if (this.notProductButton) this.notProductButton.setAction(this, function(this:DocumentExtractionLineCell){
            if (this.delegate != null) this.delegate.toggleNotAProductForRow(this._row);
        });
        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _row:DocumentExtractionReviewRow = null;
    set row(value:DocumentExtractionReviewRow){
        this._row = value;
        let line = value.line ?? {};
        let proposal = value.proposal ?? {};

        let reference = line.supplierReference ? " [" + line.supplierReference + "]" : "";
        this.descriptionLabel.text = DocumentExtractionLineCell.escape((line.description ?? "") + reference);
        this.printedLabel.text = DocumentExtractionLineCell.printedSummary(line, value.taxUnmatched == true);

        let cellLayer = this.layer as HTMLElement;
        cellLayer.classList.remove("row-matched", "row-confirm", "row-unknown", "row-not-product", "row-applied", "row-picked");

        if (value.applied) {
            this.productLabel.text = value.product != null ? value.product.name : DocumentExtractionLineCell.escape(line.description ?? "");
            this.statusLabel.text = MIOLocalizeString("ADDED", "ADDED");
            this.detailLabel.text = "";
            cellLayer.classList.add("row-applied");
        }
        else if (value.notAProduct) {
            this.productLabel.text = "";
            this.statusLabel.text = MIOLocalizeString("NOT_A_PRODUCT", "NOT A PRODUCT");
            this.detailLabel.text = MIOLocalizeString("FEE_DEPOSIT_OR_DISCOUNT_ROW", "Fee, deposit or discount row");
            cellLayer.classList.add("row-not-product");
        }
        else if (value.product == null && value.conceptItem != null) {
            this.productLabel.text = value.conceptItem.name;
            this.statusLabel.text = value.applied ? MIOLocalizeString("ADDED", "ADDED") : MIOLocalizeString("CONCEPT", "CONCEPT");
            this.detailLabel.text = MIOLocalizeString("BOOKED_AS_A_CONCEPT_LINE", "Booked as a concept line");
            cellLayer.classList.add(value.applied ? "row-applied" : "row-picked");
        }
        else if (value.product != null) {
            this.productLabel.text = value.product.name;
            this.statusLabel.text = value.picked ? MIOLocalizeString("PICKED", "PICKED")
                                  : proposal.status == "matched" ? MIOLocalizeString("MATCHED", "MATCHED") + " " + (proposal.matchedBy ?? "")
                                  : MIOLocalizeString("CONFIRM", "CONFIRM");
            this.detailLabel.text = DocumentExtractionLineCell.proposalDetail(proposal, value);
            cellLayer.classList.add(value.picked ? "row-picked" : (proposal.status == "matched" ? "row-matched" : "row-confirm"));
        }
        else {
            let candidates = (proposal.candidates ?? []) as any[];
            this.productLabel.text = candidates.length > 0 ? candidates.map((c:any) => c.name + " (" + Math.round(c.score * 100) + "%)").join(" / ") : "";
            this.statusLabel.text = candidates.length > 0 ? MIOLocalizeString("CHOOSE_ONE", "CHOOSE ONE") : MIOLocalizeString("UNKNOWN_PRODUCT", "UNKNOWN PRODUCT");
            this.detailLabel.text = "";
            cellLayer.classList.add("row-unknown");
        }

        if (this.addButton) this.addButton.hidden = value.applied || value.notAProduct;
        if (this.pickButton) this.pickButton.hidden = value.applied;
        if (this.notProductButton) {
            this.notProductButton.hidden = value.applied;
            this.notProductButton.setTitle(value.notAProduct ? MIOLocalizeString("IS_A_PRODUCT", "IS A PRODUCT") : MIOLocalizeString("NOT_A_PRODUCT", "NOT A PRODUCT"));
        }
    }

    static printedSummary(line:any, taxUnmatched:boolean = false) : string {
        let parts:string[] = [];
        if (line.quantity != null) parts.push(DocumentExtractionLineCell.escape(String(line.quantity) + (line.unit ? " " + line.unit : "")));
        if (line.packQuantity != null) parts.push("x" + line.packQuantity);
        if (line.unitPrice != null) parts.push(MIOLocalizeString("AT", "at") + " " + Number(line.unitPrice).toFixed(3).replace(/0+$/, "").replace(/\.$/, ""));
        if (line.discount != null && (line.discount.percent != null || line.discount.amount != null)) {
            parts.push("-" + (line.discount.percent != null ? line.discount.percent + "%" : line.discount.amount));
        }
        let total = line.total ?? line.base;
        if (total != null) parts.push(MIOLocalizeString("TOTAL", "TOTAL").toLowerCase() + " " + Number(total).toFixed(2));
        if (line.taxRate != null) {
            let rate = "IVA " + DocumentExtractionLineCell.escape(line.taxRate) + "%";
            parts.push(taxUnmatched ? '<span class="tax-unmatched">' + rate + " " + MIOLocalizeString("NOT_ONE_OF_OUR_TAXES", "(not one of our taxes)") + "</span>" : rate);
        }
        return parts.join("   ");
    }

    static proposalDetail(proposal:any, row:DocumentExtractionReviewRow) : string {
        let parts:string[] = [];
        let format = row.inputFormat != null ? row.inputFormat.name : proposal.inputFormatName;
        if (format) parts.push(format + (proposal.inputFormatQuantity != null ? " (" + proposal.inputFormatQuantity + ")" : ""));
        if (proposal.productQuantity != null) parts.push(Number(proposal.productQuantity).toFixed(3).replace(/0+$/, "").replace(/\.$/, "") + " " + (proposal.measureUnit ?? ""));
        if (proposal.priceDeltaPct != null) {
            let delta = Number(proposal.priceDeltaPct);
            parts.push((delta >= 0 ? "+" : "") + delta.toFixed(1) + "% " + MIOLocalizeString("VS_AGREED_PRICE", "vs agreed") + " " + (proposal.agreedPrice ?? proposal.lastPrice ?? ""));
        }
        if (proposal.productTaxRate != null && proposal.taxRate != null && Number(proposal.productTaxRate) != Number(proposal.taxRate)) {
            parts.push(MIOLocalizeString("PRODUCT_BUYS_AT", "product buys at") + " " + proposal.productTaxRate + "%");
        }
        return parts.join("   ");
    }
}
